import { Box, Stack, Typography } from '@mui/material';
import { Dropdown, RadioGroup } from 'common/form';
import {
  rulesetInputContainer,
  rulesetInputWidth
} from 'modules/analyse/components/RuleSettingItem';
import { getStatusOptions } from 'modules/analyse/utils/getStatusOptions';
import { AutomationFormItem } from 'modules/analyse/types/AutomationFormItem';
import { useRecoilValue } from 'recoil';
import { statusSetAtom } from 'services/candidate/atoms';
import {
  RequirementConditionOperator,
  RequirementConditionType
} from 'generated-graphql/types';
import { Input } from 'components/Inputs';
import { formatScoreScaled } from 'modules/analyse/utils/formatScoreScaled';
import { getScoreScaleFormatter } from 'modules/analyse-automations/utils/getScoreScaleFormatter';
import { getAnalysisAutomationScore } from 'modules/analyse-automations/utils/getAnalysisAutomationScore';
import { getAnalysisAutomationScoreOperator } from 'modules/analyse-automations/utils/getAnalysisAutomationScoreOperator';
import { getAnalysisAutomationRequirementsMetOperator } from 'modules/analyse-automations/utils/getAnalysisAutomationRequirementsMetOperator';
import { scoreConditionOptions } from 'modules/analyse/constants/scoreConditionOptions';
import { getAnalysisAutomationScoreMax } from 'modules/analyse-automations/utils/getAnalysisAutomationScoreMax';

type RuleSettingAnalysisAutomationItemProps = {
  analysisAutomationItem: AutomationFormItem;
  onChangeAnalysisAutomationItem: (value: AutomationFormItem) => void;
};

export const RuleSettingAnalysisScoreAutomationItem = ({
  analysisAutomationItem,
  onChangeAnalysisAutomationItem
}: RuleSettingAnalysisAutomationItemProps) => {
  const statusSet = useRecoilValue(statusSetAtom);

  const handleChangeCandidateStatus = (value: string) => {
    onChangeAnalysisAutomationItem({
      ...analysisAutomationItem,
      action: {
        ...analysisAutomationItem.action,
        candidateStatus: value
      }
    });
  };

  const handleChangeAnalysisAutomationOperator = (
    value: RequirementConditionOperator
  ) => {
    const newRequirementConditions =
      analysisAutomationItem.requirementConditions.map((condition) => {
        if (
          condition.type ===
          RequirementConditionType.CandidateRequirementsMetCondition
        ) {
          return { ...condition, operator: value };
        }
        return condition;
      });

    onChangeAnalysisAutomationItem({
      ...analysisAutomationItem,
      requirementConditions: newRequirementConditions
    });
  };

  const handleChangeAnalysisAutomationScoreOperator = (
    value: RequirementConditionOperator
  ) => {
    const isBetween = value === RequirementConditionOperator.Between;

    const newRequirementConditions =
      analysisAutomationItem.requirementConditions.map((condition) => {
        if (
          condition.type === RequirementConditionType.CandidateScoreCondition
        ) {
          const updatedCondition = {
            ...condition,
            operator: value
          };

          // If switching to Between, initialize scoreScaledMax if not present
          if (isBetween) {
            updatedCondition.scoreScaledMax =
              condition.scoreScaledMax ?? formatScoreScaled(100);
          } else {
            // If switching away from Between, remove scoreScaledMax
            updatedCondition.scoreScaledMax = undefined;
          }

          return updatedCondition;
        }
        return condition;
      });

    onChangeAnalysisAutomationItem({
      ...analysisAutomationItem,
      requirementConditions: newRequirementConditions
    });
  };

  const handleChangeAnalysisAutomationScore = (value: string) => {
    const parsedValue = Number(value);
    const numberValue = isNaN(parsedValue) ? 0 : parsedValue;

    const newRequirementConditions =
      analysisAutomationItem.requirementConditions.map((condition) => {
        if (
          condition.type === RequirementConditionType.CandidateScoreCondition
        ) {
          return {
            ...condition,
            scoreScaled: formatScoreScaled(numberValue)
          };
        }
        return condition;
      });

    onChangeAnalysisAutomationItem({
      ...analysisAutomationItem,
      requirementConditions: newRequirementConditions
    });
  };

  const handleChangeAnalysisAutomationScoreMax = (value: string) => {
    const parsedValue = Number(value);
    const numberValue = isNaN(parsedValue) ? 0 : parsedValue;

    const newRequirementConditions =
      analysisAutomationItem.requirementConditions.map((condition) => {
        if (
          condition.type === RequirementConditionType.CandidateScoreCondition
        ) {
          return {
            ...condition,
            scoreScaledMax: formatScoreScaled(numberValue)
          };
        }
        return condition;
      });

    onChangeAnalysisAutomationItem({
      ...analysisAutomationItem,
      requirementConditions: newRequirementConditions
    });
  };

  const analysisAutomationScore = getAnalysisAutomationScore(
    analysisAutomationItem
  );
  const analysisAutomationScoreMax = getAnalysisAutomationScoreMax(
    analysisAutomationItem
  );
  const scoreOperator = getAnalysisAutomationScoreOperator(
    analysisAutomationItem
  );
  const isBetweenOperator =
    scoreOperator === RequirementConditionOperator.Between;

  return (
    <Stack direction="column" spacing={2}>
      <Dropdown
        field={{
          containerSx: { width: rulesetInputWidth, pb: '20px' },
          label: "Change the applicant's status to",
          options: getStatusOptions({ statusSet })
        }}
        value={analysisAutomationItem.action.candidateStatus || ''}
        onChange={handleChangeCandidateStatus}
      />

      {/* NOTE: For legacy automations rules we allow users to set knockout requirements met or not met */}
      {/* But going forward you can only set knockout requirements met and then there will be a single rule for not met */}
      {getAnalysisAutomationRequirementsMetOperator(analysisAutomationItem) ===
        RequirementConditionOperator.IsFalse && (
        <RadioGroup
          field={{
            label: 'If knockout requirements are',
            options: [
              {
                label: 'Met',
                value: RequirementConditionOperator.IsTrue
              },
              {
                label: 'Not met',
                value: RequirementConditionOperator.IsFalse
              }
            ],
            sx: {
              display: 'flex',
              flexDirection: 'row'
            }
          }}
          value={getAnalysisAutomationRequirementsMetOperator(
            analysisAutomationItem
          )}
          onChange={handleChangeAnalysisAutomationOperator}
        />
      )}
      <Box sx={{ mt: '20px' }}>
        <Stack
          direction="row"
          gap={3}
          sx={{ maxWidth: 'fit-content', alignItems: 'flex-end' }}
        >
          <Dropdown
            field={{
              label: 'When knockout requirements have been met, and they score',
              options: scoreConditionOptions,
              containerSx: {
                width: rulesetInputWidth
              }
            }}
            value={scoreOperator}
            onChange={handleChangeAnalysisAutomationScoreOperator}
          />
          <Input
            type="number"
            labelContainer={{
              mb: 0
            }}
            sx={rulesetInputContainer}
            inputProps={{ min: 0, max: 100 }}
            onChange={(e) =>
              handleChangeAnalysisAutomationScore(e.target.value)
            }
            value={getScoreScaleFormatter(analysisAutomationScore)}
          />
          {isBetweenOperator && (
            <>
              <Typography sx={{ marginBottom: '15px' }}>and</Typography>
              <Input
                type="number"
                labelContainer={{
                  mb: 0
                }}
                sx={rulesetInputContainer}
                inputProps={{ min: 0, max: 100 }}
                onChange={(e) =>
                  handleChangeAnalysisAutomationScoreMax(e.target.value)
                }
                value={getScoreScaleFormatter(analysisAutomationScoreMax || 0)}
              />
            </>
          )}
          <Typography sx={{ marginBottom: '15px' }}>%</Typography>
        </Stack>
      </Box>
    </Stack>
  );
};
